import React, { Component } from "react"
import PropTypes from "prop-types"

import MDSpinner from "react-md-spinner"
import { Formik } from "formik"

import styles from "./TestDriveBookingFlow.module.sass"

import ReplacementCarSummary from "../ReplacementCarSummary"
import BookingDateTimePicker from "../BookingDateTimePicker"
import StepReplacementCar from "../StepReplacementCar/StepReplacementCar"
import TestDriveIcon from "../icons/TestDriveIcon"
import { hexToRgba } from "../../../lib/helpers"
class TestDriveForm extends Component {
  static propTypes = {
    departmentOptions: PropTypes.array,
    currentDepartment: PropTypes.string,
    replacementCarSummary: PropTypes.object,
    replacementCar: PropTypes.object,
    intendedCarChangePeriods: PropTypes.array,
    trackFunction: PropTypes.func,
    finishedCallback: PropTypes.func,
    optionalValues: PropTypes.array,
    translations: PropTypes.object,
    consents: PropTypes.array,
    consentLabel: PropTypes.string,
    displayConsentTermsAboveCta: PropTypes.bool,
    getFullName: PropTypes.bool,
    validationTranslations: PropTypes.object,
    askForPostcode: PropTypes.bool,
    datePickerProps: PropTypes.object,
    replacementCarSearch: PropTypes.object,
    handleSelectedCarChange: PropTypes.func,
    handleSelectedCarEdit: PropTypes.func,
    showReplacementCarSearch: PropTypes.bool,
    accentColor: PropTypes.string,
    departmentParamName: PropTypes.string,
  }

  state = {
    validated: false,
  }

  handleDateTimeChange = (date) => {
    this.setState({ bookingTime: date })
  }

  finished = () => {
    if (!this.props.finishedCallback) return
    this.setState({ finalized: true })

    this.props.finishedCallback(this.generateFinishedPayload())
  }

  generateFinishedPayload = (values) => {
    const buyerName =
      this.props.getFullName &&
      `${values.buyer_first_name} ${values.buyer_last_name}`
    const payload = {
      trade_in_valuation: {
        booking_time: this.state.bookingTime.toISOString(),
        buyer_name: buyerName || values.buyer_name,
        buyer_phone_number: values.buyer_phone_number,
        buyer_email: values.buyer_email,
        interested_in_trade_in_price: values.interested_in_trade_in,
        intended_car_change_period: values.intended_car_change_period,
        intended_car_change_period_freetext:
          values.intended_car_change_period_free_text,
        test_drive_wishes: values.test_drive_wishes,
        valuation_consents_attributes: {},
        extra_lead_qualification: {},
        completion_source: "test_drive_booking_form",
        email_completion_source: "test_drive_booking_form",
        booking_reason: "test_drive",
      },
    }
    payload[this.props.departmentParamName] = values.department

    if (this.props.replacementCar) {
      payload["trade_in_valuation"]["car_id"] = this.props.replacementCar.id
    }

    if (values.postcode) {
      payload["trade_in_valuation"]["extra_lead_qualification"]["postcode"] =
        values.postcode
    }

    this.props.consents.forEach((consent, i) => {
      payload["trade_in_valuation"]["valuation_consents_attributes"][`${i}`] = {
        agreed: values[`consent-${i}`].length > 0 ? 1 : 0,
        trade_in_consent_id: values[`consent-${i}-id`],
      }
    })
    return payload
  }

  render() {
    const nameValues = this.props.getFullName
      ? { buyer_first_name: "", buyer_last_name: "" }
      : { buyer_name: "" }

    const otherValues = {
      buyer_phone_number: "",
      buyer_email: "",
      interested_in_trade_in: "",
      intended_car_change_period: "",
      intended_car_change_period_free_text: "",
      test_drive_wishes: "",
    }

    if (this.props.askForPostcode) {
      otherValues["postcode"] = ""
    }

    this.props.consents.forEach((consent, i) => {
      otherValues[`consent-${i}`] = []
      otherValues[`consent-${i}-id`] = consent.trade_in_consent_id
    })

    // Initial values are in the order of the form fields
    // This ensures that when we scroll to the top error, it always picks the very first one
    const initialValues = {
      // Assign initial department based on the props
      // When the departement selection is disabled, we still want to show the right department if selected previously
      department: this.props.currentDepartment,
      ...nameValues,
      ...otherValues,
    }

    const refs = Object.keys(initialValues).reduce((acc, value) => {
      acc[value] = React.createRef()
      return acc
    }, {})

    const scrollToError = (id) =>
      refs[id]?.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      })

    return (
      <div className={styles.container}>
        <React.Fragment>
          <div className={styles.titleContainer}>
            <div className={styles.title}>
              <b>{this.props.translations.headline}</b>
            </div>
            <TestDriveIcon
              accentColor={hexToRgba(this.props.accentColor, 0.1)}
            />
          </div>
        </React.Fragment>
        <Formik
          enableReinitialize={true} // Formik should reset the form if initialValues changes, used to correctly assign department when changed/picked
          validateOnChange={this.state.validated}
          validateOnBlur={this.state.validated}
          initialValues={initialValues}
          validate={(values) => {
            let optionalValues = [...this.props.optionalValues]
            if (!this.props.departmentOptions) {
              optionalValues = [...optionalValues, "department"]
            }
            const errors = {}
            Object.keys(values)
              .filter((value) => !optionalValues.includes(value))
              .map((key) => {
                if (!values[key]) {
                  errors[key] = this.props.validationTranslations.required
                }
              })
            this.props.consents.map((consent, i) => {
              if (!values[`consent-${i}`].length > 0) {
                errors[`consent-${i}`] =
                  this.props.validationTranslations.required
              }
            })
            if (
              values.buyer_email &&
              !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(
                values.buyer_email
              )
            ) {
              errors.buyer_email = this.props.validationTranslations.email
            }

            if (
              values.intended_car_change_period === "other" &&
              !values.intended_car_change_period_free_text
            ) {
              errors.intended_car_change_period_free_text =
                this.props.validationTranslations.required
            }

            if (Object.keys(errors).length > 0) {
              scrollToError(Object.keys(errors)[0])
            }

            this.setState({ validated: true })
            return errors
          }}
          onSubmit={(values, { setSubmitting }) => {
            setTimeout(() => {
              this.setState({ finalized: true })
              this.props.finishedCallback(this.generateFinishedPayload(values))
              setSubmitting(false)
            }, 400)
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isValidating,
          }) => (
            <form onSubmit={handleSubmit}>
              {/* If there is a replacement car, show car summary */}
              {this.props.replacementCar &&
              !this.props.showReplacementCarSearch ? (
                <div style={{ marginBottom: "6px" }}>
                  <ReplacementCarSummary
                    replacementCar={this.props.replacementCar}
                    replacementCarSearchAvailable={
                      this.props.replacementCarSearch
                    }
                    strings={this.props.replacementCarSummary}
                    onEditReplacementCar={this.props.handleSelectedCarEdit}
                  />
                </div>
              ) : (
                // If there is no replacement car, show replacement car selection
                // only when replacement car search is enabled
                this.props.replacementCarSearch && (
                  <React.Fragment>
                    <div className={styles.pickCarHeadline}>
                      {this.props.translations.chooseCarForTestDrive}
                    </div>
                    <StepReplacementCar
                      {...this.props.replacementCarSearch}
                      onSelectedCarChange={this.props.handleSelectedCarChange}
                      fromBookTestDrive
                    />
                  </React.Fragment>
                )
              )}
              {/* When replacementCarSearch is false
                or when replacement car is picked and showReplacementCarSearch is false
                show the form */}
              {((this.props.replacementCar &&
                !this.props.showReplacementCarSearch) ||
                !this.props.replacementCarSearch) && (
                <div>
                  <BookingDateTimePicker
                    translations={this.props.translations}
                    datePickerProps={this.props.datePickerProps}
                    onDateTimeChange={this.handleDateTimeChange}
                    styles={styles}
                  />
                  {this.props.departmentOptions?.length > 1 && (
                    <div className="form-row">
                      <label htmlFor="department" className={styles.label}>
                        {this.props.translations.labels.department}
                      </label>
                      <select
                        onChange={handleChange}
                        name="department"
                        id="department"
                        disabled={this.props.replacementCar}
                        className={`Dropdown-control select-department full-width ${
                          errors.department && touched.department
                            ? "invalid"
                            : values.department && touched.department
                            ? "valid"
                            : ""
                        }`}
                        value={values.department}
                        ref={refs["department"]}
                      >
                        <option value="">
                          {this.props.translations.labels.department}
                        </option>
                        {this.props.departmentOptions.map((option) => (
                          <option value={option.value} key={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <div className="invalid">
                        {errors.department &&
                          touched.department &&
                          errors.department}
                      </div>
                    </div>
                  )}
                  {this.props.getFullName ? (
                    <React.Fragment>
                      <div className="form-row">
                        <label className={styles.label}>
                          {this.props.translations.labels.buyer_first_name}
                        </label>
                        <input
                          type="text"
                          name="buyer_first_name"
                          placeholder={
                            this.props.translations.labels
                              .buyer_first_name_placeholder
                          }
                          ref={refs["buyer_first_name"]}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={
                            errors.buyer_first_name && touched.buyer_first_name
                              ? "invalid"
                              : values.buyer_first_name &&
                                touched.buyer_first_name
                              ? "valid"
                              : ""
                          }
                        />
                        <div className="invalid">
                          {errors.buyer_first_name &&
                            touched.buyer_first_name &&
                            errors.buyer_first_name}
                        </div>
                      </div>
                      <div className="form-row">
                        <label className={styles.label}>
                          {this.props.translations.labels.buyer_last_name}
                        </label>
                        <input
                          type="text"
                          name="buyer_last_name"
                          placeholder={
                            this.props.translations.labels
                              .buyer_last_name_placeholder
                          }
                          ref={refs["buyer_last_name"]}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          className={
                            errors.buyer_last_name && touched.buyer_last_name
                              ? "invalid"
                              : values.buyer_last_name &&
                                touched.buyer_last_name
                              ? "valid"
                              : ""
                          }
                        />
                        <div className="invalid">
                          {errors.buyer_last_name &&
                            touched.buyer_last_name &&
                            errors.buyer_last_name}
                        </div>
                      </div>
                    </React.Fragment>
                  ) : (
                    <div className="form-row">
                      <label htmlFor="buyer_name" className={styles.label}>
                        {this.props.translations.labels.buyer_name}
                      </label>
                      <input
                        type="text"
                        name="buyer_name"
                        id="buyer_name"
                        placeholder={
                          this.props.translations.labels.buyer_name_placeholder
                        }
                        ref={refs["buyer_name"]}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={
                          errors.buyer_name && touched.buyer_name
                            ? "invalid"
                            : values.buyer_name && touched.buyer_name
                            ? "valid"
                            : ""
                        }
                      />
                      <div className="invalid">
                        {errors.buyer_name &&
                          touched.buyer_name &&
                          errors.buyer_name}
                      </div>
                    </div>
                  )}
                  <div className="form-row">
                    <label
                      htmlFor="buyer_phone_number"
                      className={styles.label}
                    >
                      {this.props.translations.labels.buyer_phone_number}
                    </label>
                    <input
                      type="text"
                      ref={refs["buyer_phone_number"]}
                      name="buyer_phone_number"
                      id="buyer_phone_number"
                      placeholder={
                        this.props.translations.labels
                          .buyer_phone_number_placeholder
                      }
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={
                        errors.buyer_phone_number && touched.buyer_phone_number
                          ? "invalid"
                          : values.buyer_phone_number &&
                            touched.buyer_phone_number
                          ? "valid"
                          : ""
                      }
                    />
                    <div className="invalid">
                      {errors.buyer_phone_number &&
                        touched.buyer_phone_number &&
                        errors.buyer_phone_number}
                    </div>
                  </div>
                  <div className="form-row">
                    <label htmlFor="buyer_email" className={styles.label}>
                      {this.props.translations.labels.buyer_email}
                    </label>
                    <input
                      type="text"
                      name="buyer_email"
                      id="buyer_email"
                      placeholder={
                        this.props.translations.labels.buyer_email_placeholder
                      }
                      ref={refs["buyer_email"]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={
                        errors.buyer_email && touched.buyer_email
                          ? "invalid"
                          : values.buyer_email && touched.buyer_email
                          ? "valid"
                          : ""
                      }
                    />
                    <div className="invalid">
                      {errors.buyer_email &&
                        touched.buyer_email &&
                        errors.buyer_email}
                    </div>
                  </div>
                  {this.props.askForPostcode && (
                    <div className="form-row">
                      <label htmlFor="postcode" className={styles.label}>
                        {this.props.translations.labels.postcode}
                      </label>
                      <input
                        type="text"
                        name="postcode"
                        id="postcode"
                        ref={refs["postcode"]}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={
                          errors.postcode && touched.postcode
                            ? "invalid"
                            : values.postcode && touched.postcode
                            ? "valid"
                            : ""
                        }
                      />
                      <div className="invalid">
                        {errors.postcode && touched.postcode && errors.postcode}
                      </div>
                    </div>
                  )}
                  <div className="form-row">
                    <label htmlFor="test_drive_wishes" className={styles.label}>
                      {this.props.translations.labels.test_drive_wishes}
                    </label>
                    <textarea
                      name="test_drive_wishes"
                      id="test_drive_wishes"
                      placeholder={
                        this.props.translations.labels
                          .test_drive_wishes_placeholder
                      }
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`full-width ${
                        errors.test_drive_wishes && touched.test_drive_wishes
                          ? "invalid"
                          : ""
                      }
                    `}
                    />
                  </div>
                  <div className="form-row">
                    <label>
                      {this.props.translations.labels.interested_in_trade_in}
                    </label>
                    <div className="radio-button-group-2 obvious horizontal">
                      <label
                        htmlFor="interested_in_trade_in_true"
                        className={`btn-grouped themed_v2 ${
                          this.state.interestedInTradeIn === "true"
                            ? "active"
                            : ""
                        }`}
                      >
                        <input
                          name="interested_in_trade_in"
                          onChange={handleChange}
                          type="radio"
                          id="interested_in_trade_in_true"
                          defaultValue="true"
                          required
                          checked={values.interested_in_trade_in === "true"}
                        />
                        {this.props.translations.labels.yes}
                      </label>
                      <label
                        htmlFor="interested_in_trade_in_false"
                        className={`btn-grouped themed_v2 ${
                          this.state.interestedInTradeIn === "false"
                            ? "active"
                            : ""
                        }`}
                      >
                        <input
                          name="interested_in_trade_in"
                          onChange={handleChange}
                          type="radio"
                          defaultValue="false"
                          required
                          id="interested_in_trade_in_false"
                          checked={values.interested_in_trade_in === "false"}
                        />
                        {this.props.translations.labels.no}
                      </label>
                    </div>
                  </div>

                  <div className="form-row">
                    <label>
                      {
                        this.props.translations.intendedCarChangePeriodTexts
                          .question
                      }
                    </label>
                    <div
                      className={`intended-car-change-period-options radio-button-group obvious ${
                        errors.intended_car_change_period &&
                        touched.intended_car_change_period
                          ? styles.invalid
                          : ""
                      }`}
                    >
                      {this.props.intendedCarChangePeriods.map((k) => {
                        return (
                          <label
                            key={k}
                            htmlFor={k}
                            className={`radio-inline ${k} btn-grouped themed_v2 ${
                              values.intended_car_change_period === k
                                ? "active selected"
                                : ""
                            }`}
                          >
                            <input
                              id={k}
                              name="intended_car_change_period"
                              key={k}
                              ref={refs["intended_car_change_period"]}
                              type="radio"
                              value={k}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              checked={values.intended_car_change_period === k}
                            />
                            {
                              this.props.translations
                                .intendedCarChangePeriodTexts[k]
                            }
                          </label>
                        )
                      })}
                      {values.intended_car_change_period === "other" && (
                        <React.Fragment>
                          <input
                            name="intended_car_change_period_free_text"
                            type="text"
                            ref={refs["intended_car_change_period_free_text"]}
                            style={{ marginTop: 0 }}
                            className="option-other-text-area"
                            placeholder={
                              this.props.translations
                                .intendedCarChangePeriodTexts[
                                "other_freetext_placeholder"
                              ]
                            }
                            onChange={handleChange}
                          />
                          <div className="invalid">
                            {errors.intended_car_change_period_free_text &&
                              touched.intended_car_change_period_free_text &&
                              errors.intended_car_change_period_free_text}
                          </div>
                        </React.Fragment>
                      )}
                    </div>
                    <div className="invalid">
                      {errors.intended_car_change_period &&
                        touched.intended_car_change_period &&
                        errors.intended_car_change_period}
                    </div>
                  </div>
                  <div className="form-row">
                    {this.props.consentLabel && this.props.consentLabel}
                    {this.props.consents.map((consent, i) => {
                      return (
                        <React.Fragment key={`consent-${i}`}>
                          <label
                            className={`au-user-privacy-consent ${
                              consent.required ? "required" : ""
                            }`}
                          >
                            <input
                              type="checkbox"
                              name={`consent-${i}`}
                              ref={refs[`consent-${i}`]}
                              checked={values[`consent-${i}`].length > 0}
                              value={`consent-${i}`}
                              required="required"
                              aria-required="true"
                              onChange={handleChange}
                            />
                            <p
                              dangerouslySetInnerHTML={{
                                __html: consent.text,
                              }}
                            />
                            {!this.props.displayConsentTermsAboveCta &&
                              consent.terms && (
                                <span className="footnote-number">{i + 1}</span>
                              )}
                          </label>
                          {consent.terms &&
                            this.props.displayConsentTermsAboveCta && (
                              <p
                                className={`au-user-privacy-terms above-cta ${
                                  consent.required ? "required" : ""
                                }`}
                                dangerouslySetInnerHTML={{
                                  __html: consent.terms,
                                }}
                              />
                            )}
                          <div
                            className="invalid"
                            style={{
                              marginBottom: "5px",
                              paddingLeft: "25px",
                            }}
                          >
                            {errors[`consent-${i}`] &&
                              touched[`consent-${i}`] &&
                              errors[`consent-${i}`]}
                          </div>
                        </React.Fragment>
                      )
                    })}
                  </div>
                  <div className="form-row">
                    <button
                      type="submit"
                      className="main-cta"
                      onClick={handleSubmit}
                    >
                      {isValidating || this.state.finalized ? (
                        <MDSpinner
                          singleColor="#fff"
                          size={18}
                          className={styles.buttonSpinner}
                        />
                      ) : (
                        <span className={styles.submitCta}>
                          {this.props.translations.cta}
                        </span>
                      )}
                    </button>
                  </div>
                  {!this.props.displayConsentTermsAboveCta && (
                    <div className="form-row">
                      {this.props.consents.map((consent, i) => {
                        return (
                          <React.Fragment key={`consent-term-${i}`}>
                            {consent.terms && (
                              <React.Fragment>
                                <span className="footnote-number terms">
                                  {i + 1}
                                </span>
                                <p
                                  className={`au-user-privacy-terms ${
                                    consent.required ? "required" : ""
                                  }`}
                                  dangerouslySetInnerHTML={{
                                    __html: consent.terms,
                                  }}
                                />
                              </React.Fragment>
                            )}
                          </React.Fragment>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </form>
          )}
        </Formik>
      </div>
    )
  }
}

export default TestDriveForm
